const TC_RE = /(\d{1,2}(?:[:.]\d{2}){1,3})/g;
const CAM_RE = /\b(cam(?:era)?\s*\d+)\b/i;
const WA_PREFIX = [/^\[\d{1,2}:\d{2}(?::\d{2})?(?:,\s*[^\]]{4,24})?\]\s*[^:\n]{1,40}:\s*/, /^\d{1,2}[./]\d{1,2}[./]\d{2,4},?\s+\d{1,2}:\d{2}(?:\s*[ap]m)?\s*[-–]\s*[^:\n]{1,40}:\s*/i];

export const normLine = (line) => {
  let s = String(line || "").replace(/[*_~`]/g, "").trim();
  WA_PREFIX.forEach((re) => { s = s.replace(re, ""); });
  s = s.replace(/^\p{Extended_Pictographic}[\uFE0F\u200D\p{Extended_Pictographic}]*\s*/u, "").replace(/^[\s•·▪►▶→>\-–—]+/, "").replace(/^\d{1,2}[.)]\s+(?=\D|\d{1,2}[:.,]\d{2})/, "");
  s = s.replace(/\b(\d{1,2})\s*m(?:in)?\s*(\d{1,2})\s*s(?:ec)?\b/gi, (_, m, sec) => `${m}:${sec.padStart(2, "0")}`)
    .replace(/\b(\d{1,2})\s*m(?:in)?\b(?![a-z])/gi, "$1:00").replace(/(^|[\s(])(\d{1,2}),(\d{2})(?=$|[\s\-–—)])/g, "$1$2:$3")
    .replace(/\bc\s?(\d{1,2})\b/gi, "CAM $1").replace(/\s*(?:→|->|=>)\s*/g, " - ");
  return s.trim();
};

export const unparsedLines = (text) => String(text || "").split(/\r?\n/).map(normLine)
  .filter((l) => l && !l.match(TC_RE) && !/^cam(era)?\s*\d+\s*:?$/i.test(l));

const toSec = (raw, fps) => {
  const p = raw.split(/[:.]/).map(Number);
  if (p.length === 4) return p[0] * 3600 + p[1] * 60 + p[2] + p[3] / fps;
  return p.reduce((a, x) => a * 60 + x, 0);
};

// Each line: optional CAM, "start [- end]", note. Lines without a time are appended to the previous note.
export const parseTimecodes = (text, fps = 25) => {
  const out = [];
  let lastCam = "";
  for (const line of String(text || "").split(/\r?\n/)) {
    const clean = normLine(line);
    if (!clean) continue;
    const cm = clean.match(CAM_RE);
    if (cm) lastCam = cm[1].replace(/^camera/i, "CAM").replace(/\s+/g, " ").toUpperCase().replace(/CAM(\d)/, "CAM $1");
    const times = [...clean.matchAll(TC_RE)];
    if (!times.length) {
      if (out.length && !cm) out[out.length - 1].note = `${out[out.length - 1].note} ${clean}`.trim();
      continue;
    }
    const start = toSec(times[0][1], fps);
    const between = times[1] ? clean.slice(times[0].index + times[0][1].length, times[1].index) : "";
    const isRange = times[1] && /^\s*(-|–|—|→|->|>|pana la|până la|la|to)?\s*$/i.test(between);
    const end = isRange ? toSec(times[1][1], fps) : null;
    const used = isRange ? times[1].index + times[1][1].length : times[0].index + times[0][1].length;
    const note = (clean.slice(0, times[0].index) + " " + clean.slice(used))
      .replace(CAM_RE, "").replace(/^[\s\-–—:|,.()[\]]+|[\s\-–—:|,([]+$/g, "").replace(/\s{2,}/g, " ").trim();
    out.push({ cam: lastCam, start, end: end != null && end > start ? end : null, note });
  }
  return out;
};

const pad = (n, l = 2) => String(Math.floor(n)).padStart(l, "0");
const frames = (sec, fps) => Math.round(sec * fps);
const smpte = (sec, fps, offsetH = 0) => {
  const f = frames(sec, fps) + offsetH * 3600 * fps;
  const s = Math.floor(f / fps);
  return `${pad(s / 3600)}:${pad((s % 3600) / 60)}:${pad(s % 60)}:${pad(f % fps)}`;
};
const label = (t) => [t.cam, t.note].filter(Boolean).join(" · ") || "Timecode";
const xml = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export const toPremiereXML = (items, name, fps) => {
  const dur = Math.max(...items.map((t) => frames((t.end ?? t.start) + 10, fps)), fps * 60);
  const rate = `<rate><timebase>${fps}</timebase><ntsc>FALSE</ntsc></rate>`;
  const markers = items.map((t) => `    <marker><name>${xml(label(t))}</name><comment>${xml(t.note)}</comment><in>${frames(t.start, fps)}</in><out>${t.end != null ? frames(t.end, fps) : -1}</out></marker>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>
<!DOCTYPE xmeml>
<xmeml version="5">
  <sequence id="sequence-1">
    <name>${xml(name)} - timecode</name>
    <duration>${dur}</duration>
    ${rate}
    <timecode>${rate}<string>00:00:00:00</string><frame>0</frame><displayformat>NDF</displayformat></timecode>
    <media>
      <video><format><samplecharacteristics>${rate}<width>1920</width><height>1080</height></samplecharacteristics></format><track/></video>
      <audio><track/></audio>
    </media>
${markers}
  </sequence>
</xmeml>
`;
};

export const toResolveEDL = (items, name, fps) => {
  const rows = items.map((t, i) => {
    const d = Math.max(1, t.end != null ? frames(t.end - t.start, fps) : 1);
    const a = smpte(t.start, fps, 1);
    const b = smpte(t.start + 1 / fps, fps, 1);
    const color = t.end != null ? "ResolveColorGreen" : "ResolveColorYellow";
    return `${pad(i + 1, 3)}  001      V     C        ${a} ${b} ${a} ${b}  \n |C:${color} |M:${label(t).replace(/\|/g, "/")} |D:${d}\n`;
  });
  return `TITLE: ${name} - timecode\nFCM: NON-DROP FRAME\n\n${rows.join("\n")}`;
};

const srtTime = (sec) => {
  const ms = Math.round(sec * 1000);
  return `${pad(ms / 3600000)}:${pad((ms % 3600000) / 60000)}:${pad((ms % 60000) / 1000)},${pad(ms % 1000, 3)}`;
};

export const toSRT = (items) => [...items].sort((a, b) => a.start - b.start).map((t, i) =>
  `${i + 1}\n${srtTime(t.start)} --> ${srtTime(t.end ?? t.start + 2)}\n${label(t)}\n`).join("\n");

export const itemsToText = (items) => items.map((t) => {
  const f = (s) => { const v = Math.floor(s); const h = Math.floor(v / 3600); return `${h ? `${h}:` : ""}${pad((v % 3600) / 60)}:${pad(v % 60)}`; };
  return [t.cam, t.kind === "range" && t.end != null ? `${f(t.start)} - ${f(t.end)}` : f(t.start), t.note].filter(Boolean).join(" ");
}).join("\n");

export const toFCPXML = (items, name, fps) => {
  const r = (sec) => `${Math.round(sec * fps)}/${fps}s`;
  const sorted = [...items].sort((a, b) => a.start - b.start);
  const dur = Math.max(60, ...sorted.map((t) => (t.end ?? t.start) + 5));
  const esc = (s) => String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/"/g, "&quot;");
  const marks = sorted.map((t) => `          <marker start="${r(t.start)}" duration="${r(t.end != null ? t.end - t.start : 1 / fps)}" value="${esc([t.cam, t.note].filter(Boolean).join(" · ") || "Timecode")}"/>`).join("\n");
  return `<?xml version="1.0" encoding="UTF-8"?>\n<!DOCTYPE fcpxml>\n<fcpxml version="1.9">\n  <resources><format id="r1" frameDuration="1/${fps}s" width="1080" height="1920"/></resources>\n  <library><event name="${esc(name)}"><project name="${esc(name)} - timecode">\n    <sequence format="r1" duration="${r(dur)}"><spine>\n        <gap name="Timecode" offset="0s" duration="${r(dur)}">\n${marks}\n        </gap>\n    </spine></sequence>\n  </project></event></library>\n</fcpxml>\n`;
};

export const download = (content, filename, type) => {
  const url = URL.createObjectURL(new Blob([content], { type }));
  const a = Object.assign(document.createElement("a"), { href: url, download: filename });
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};
